import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import StatusDropdown from '../../../../shared/StatusDropdown';
import PriorityDropdown from '../../../TaskNode/PriorityDropdown';
import DifficultyDropdown from '../../../TaskNode/DifficultyDropdown';
import TagsDropdown from '../../../TaskNode/TagsDropdown';
import DateTimePopover, { dateFieldTitle } from '../../../../../ui/DateTimePopover';
import { announceDatePopover, onOtherDatePopover } from '../../../../../ui/DateTimePopover/popoverBus';
import {
  EstimatedDurationInputs,
  useRelativeTimeTick,
} from '../../../../shared/CardComponents/DueDateDisplay';
import { useTaskEstimatedDuration } from '../../../../shared/CardComponents/DueDateDisplay/hooks/useTaskEstimatedDuration';
import { formatRelativeTime } from '../../../../shared/CardComponents/DueDateDisplay/utils/formatRelativeTime';
import { formatDueDateDisplay, parseMissionDate } from '../../../../../../utils/missionDates';
import { dateControlRect, getEdgeAwarePosition } from '../../../../shared/CardComponents/DueDateDisplay/utils/getEdgeAwarePosition';
import { formatDurationRange } from '../../../../../../utils/taskDuration';
import RemindMeRow from '../../../../shared/CardComponents/RemindMeRow';
import './MetadataBar.css';

/**
 * MetadataBar Component
 * 
 * Two grouped sections:
 * 1. Properties: Status, Priority, Difficulty, Tags
 * 2. Dates: Due date, Est. days, Created, Started, Completed, Updated
 */

// Calendar icon
const CalendarIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Clock icon
const ClockIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MetadataBar = ({
  task,
  variant = 'inline',
  statuses = [],
  allTasks = [],
  allCategories = [],
  allTags = [],
  // Lock state props for dependency warning
  isLocked = false,
  lockReason,
  blockingDependencies = [],
  hasNestedSprint = false,
  nestedSprint = null,
  nestedSprints = [],
  allSprints = [],
  onStatusChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  onPriorityChange,
  onDifficultyChange,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onSubmitDeliverable,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onStartedAtChange,
  onCompletedAtChange,
}) => {
  useRelativeTimeTick();
  const {
    minutes: estimatedMinutes,
    minMinutes: estimatedMinMinutes,
    maxMinutes: estimatedMaxMinutes,
    isNestedRollup,
  } = useTaskEstimatedDuration(task, {
    statuses,
    nestedSprints,
    allSprints,
  });

  const rootClass =
    variant === 'sidebar'
      ? 'mfv-metadata-bar mfv-metadata-bar--sidebar'
      : 'mfv-metadata-bar';

  const [openDatePicker, setOpenDatePicker] = useState(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const dateRefs = useRef({});
  const portalRef = useRef(null);
  const popoverId = `meta-${task?.id || 'task'}-${openDatePicker || ''}`;

  const dueDateFormatted = formatDueDateDisplay(task.dueDate);

  const handleDateClick = (fieldId, showTime = false) => (e) => {
    e.stopPropagation();
    const rect = dateControlRect(dateRefs.current[fieldId]);
    if (rect) {
      const popupHeight = showTime ? 220 : 180;
      setPickerPosition(getEdgeAwarePosition(rect, 280, popupHeight));
    }
    announceDatePopover(`meta-${task?.id || 'task'}-${fieldId}`);
    setOpenDatePicker(fieldId);
  };

  const handleDateChange = (handler, showTime) => (date) => {
    if (!date || !handler) return;
    const isoDate = showTime ? date.toISOString() : date.toISOString().split('T')[0];
    handler(task.id, isoDate);
  };

  const handleDateClear = (handler) => () => {
    handler?.(task.id, null);
    setOpenDatePicker(null);
  };

  useEffect(() => {
    if (!openDatePicker) return;
    
    const handleClickOutside = (e) => {
      if (portalRef.current?.contains(e.target)) return;
      if (dateRefs.current[openDatePicker]?.contains(e.target)) return;
      setOpenDatePicker(null);
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenDatePicker(null);
    };
    
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDatePicker]);

  useEffect(() => {
    if (!openDatePicker) return undefined;
    return onOtherDatePopover(popoverId, () => setOpenDatePicker(null));
  }, [openDatePicker, popoverId]);

  useLayoutEffect(() => {
    if (!openDatePicker) return undefined;
    const place = () => {
      const anchor = dateRefs.current[openDatePicker];
      const pop = portalRef.current;
      const rect = dateControlRect(anchor);
      if (!rect) return;
      const width = pop?.offsetWidth || 280;
      const height = pop?.offsetHeight || 200;
      setPickerPosition(getEdgeAwarePosition(rect, width, height));
    };
    place();
    const pop = portalRef.current;
    const observer = pop && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(place)
      : null;
    if (pop && observer) observer.observe(pop);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [openDatePicker]);

  // Date fields - excludes createdAt and updatedAt (shown below title)
  const dateFields = [
    { id: 'startedAt', label: 'Started', value: task.startedAt, handler: onStartedAtChange, showTime: true, placeholder: '—' },
    { id: 'completedAt', label: 'Completed', value: task.completedAt, handler: onCompletedAtChange, showTime: true, placeholder: '—' },
  ];

  return (
    <div className={rootClass}>
      {/* Group 1: Properties - Status, Priority, Difficulty, Tags */}
      <div className="mfv-metadata-bar__group mfv-metadata-bar__group--properties">
        <div className="mfv-metadata-bar__field">
          <span className="mfv-metadata-bar__label">Status</span>
          <StatusDropdown
            variant="button"
            taskId={task.id}
            taskName={task.title || task.name || 'this mission'}
            currentStatus={task.status}
            statuses={statuses}
            allTasks={allTasks}
            isLocked={isLocked}
            lockReason={lockReason}
            blockingDependencies={blockingDependencies}
            hasNestedSprint={hasNestedSprint}
            nestedSprintProgress={nestedSprint?.progress}
            statusAutoSync={task.statusAutoSync}
            onStatusChange={onStatusChange}
            onAddStatus={onAddStatus}
            onDeleteStatus={onDeleteStatus}
            onRenameStatus={onRenameStatus}
            onSetStatusAutoSync={onSetStatusAutoSync}
            deliverables={task.deliverables || []}
            onSubmitDeliverable={onSubmitDeliverable}
            showLabel={false}
          />
        </div>

        <div className="mfv-metadata-bar__field">
          <span className="mfv-metadata-bar__label">Priority</span>
          <PriorityDropdown
            taskId={task.id}
            currentPriority={task.priority}
            onPriorityChange={onPriorityChange}
            showLabel={false}
          />
        </div>

        <div className="mfv-metadata-bar__field">
          <span className="mfv-metadata-bar__label">Difficulty</span>
          <DifficultyDropdown
            taskId={task.id}
            currentDifficulty={task.difficulty}
            onDifficultyChange={onDifficultyChange}
            showLabel={false}
          />
        </div>

        <div className="mfv-metadata-bar__field mfv-metadata-bar__field--tags">
          <span className="mfv-metadata-bar__label">Tags</span>
          <div className="mfv-metadata-bar__tags-value">
            <TagsDropdown
              taskId={task.id}
              task={task}
              allTags={allTags.length > 0 ? allTags : allCategories}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
              onSetPrimaryTag={onSetPrimaryTag}
              onRenameTag={onRenameTag}
              onDeleteTag={onDeleteTag}
            />
          </div>
        </div>
      </div>

      {/* Group 2: Dates - Due, Est. days, Created, Started, Completed, Updated */}
      <div className="mfv-metadata-bar__group mfv-metadata-bar__group--dates">
        {/* Due Date */}
        <div className="mfv-metadata-bar__field">
          <span className="mfv-metadata-bar__label">Due</span>
          <button
            ref={el => dateRefs.current['dueDate'] = el}
            className={`mfv-metadata-bar__date-btn ${dueDateFormatted?.status ? `mfv-metadata-bar__date-btn--${dueDateFormatted.status}` : ''} ${!task.dueDate ? 'mfv-metadata-bar__date-btn--empty' : ''}`}
            onClick={handleDateClick('dueDate', true)}
          >
            <CalendarIcon size={12} />
            <span>{dueDateFormatted?.text || 'Set'}</span>
          </button>
          
          {openDatePicker === 'dueDate' && createPortal(
            <div
              ref={portalRef}
              className="mfv-metadata-bar__date-picker-portal"
              style={{ position: 'fixed', top: pickerPosition.top, left: pickerPosition.left, zIndex: 10000 }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <DateTimePopover
                title="Due date"
                chooseMode
                countdownCommits
                countdownApplyLabel="Set due date"
                selected={task.dueDate ? parseMissionDate(task.dueDate) : null}
                onChange={handleDateChange(onDueDateChange, true)}
                onDismiss={() => setOpenDatePicker(null)}
                onClear={handleDateClear(onDueDateChange)}
                showClear
                showTimeSelect
              />
            </div>,
            document.body
          )}
        </div>

        {/* Estimated duration */}
        <div className="mfv-metadata-bar__field">
          <span className="mfv-metadata-bar__label">Est. time</span>
          <EstimatedDurationInputs
            task={task}
            minutes={estimatedMinutes}
            readOnly={isNestedRollup}
            readOnlyText={isNestedRollup
              ? (estimatedMaxMinutes != null
                ? formatDurationRange(estimatedMinMinutes ?? 0, estimatedMaxMinutes)
                : '—')
              : undefined}
            readOnlyTitle={
              isNestedRollup && estimatedMaxMinutes != null && (estimatedMinMinutes ?? 0) !== estimatedMaxMinutes
                ? 'Fastest to longest route through open choices'
                : 'Total estimated time from missions in nested campaign'
            }
            classPrefix="mfv-metadata-bar"
            inputClassName="mfv-metadata-bar__number-input"
            onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
          />
        </div>

        {/* Timestamp fields - Started at & Completed at */}
        {dateFields.map((field) => {
          const displayValue = field.value ? formatRelativeTime(field.value) : field.placeholder;
          const hasValue = !!field.value;
          const isClickable = !!field.handler && !field.readOnly;

          return (
            <div key={field.id} className="mfv-metadata-bar__field">
              <span className="mfv-metadata-bar__label">{field.label}</span>
              <span
                ref={el => dateRefs.current[field.id] = el}
                className={`mfv-metadata-bar__date-value ${!hasValue ? 'mfv-metadata-bar__date-value--empty' : ''} ${isClickable ? 'mfv-metadata-bar__date-value--clickable' : ''}`}
                onClick={isClickable ? handleDateClick(field.id, field.showTime) : undefined}
                title={field.value ? new Date(field.value).toLocaleString() : undefined}
              >
                <ClockIcon size={11} />
                <span>{displayValue || '—'}</span>
              </span>

              {openDatePicker === field.id && isClickable && createPortal(
                <div
                  ref={portalRef}
                  className="mfv-metadata-bar__date-picker-portal"
                  style={{ position: 'fixed', top: pickerPosition.top, left: pickerPosition.left, zIndex: 10000 }}
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <DateTimePopover
                    title={dateFieldTitle(field.label)}
                    selected={field.value ? parseMissionDate(field.value) : null}
                    onChange={handleDateChange(field.handler, field.showTime)}
                    onDismiss={() => setOpenDatePicker(null)}
                    onClear={handleDateClear(field.handler)}
                    showClear
                    showTimeSelect={field.showTime}
                  />
                </div>,
                document.body
              )}
            </div>
          );
        })}

        {onRemindersChange && (
          <RemindMeRow
            variant="sidebar"
            task={task}
            onRemindersChange={onRemindersChange}
            isLocked={isLocked}
            lockReason={lockReason}
          />
        )}
      </div>
    </div>
  );
};

export default MetadataBar;
